/**
 * Outbound webhooks.
 *
 * Two things this screen has to get right, and they pull in opposite
 * directions. The signing secret is shown exactly once, so the reveal is
 * modelled on ApiKeys and is deliberately awkward to dismiss. And an endpoint
 * that has silently stopped receiving is worse than one that visibly failed —
 * so failure counts and auto-disabling are on the row, not behind a click.
 */

import { useState, type FormEvent } from "react";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { CopyValue } from "../components/CopyValue";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import type {
  CreatedWebhookEndpoint,
  CursorPage,
  WebhookDelivery,
  WebhookEndpoint,
} from "../api/types";

/**
 * The event types worth offering as checkboxes.
 *
 * Not exhaustive by design — `POST /v1/events` lets a tenant record their own
 * types, and an endpoint with an empty list receives everything, which is how
 * those reach a receiver. Listing every possible string here would be a lie
 * that goes stale.
 */
const DELIVERY_EVENTS = [
  "sent",
  "delivered",
  "bounced",
  "complained",
  "failed",
  "opened",
  "clicked",
  "unsubscribed",
];

export function Webhooks() {
  const { orgId, can } = useSession();
  const [created, setCreated] = useState<CreatedWebhookEndpoint | null>(null);
  const admin = can("webhooks:manage");

  const endpoints = useAsync<WebhookEndpoint[]>(
    // Same reasoning as ApiKeys: without the role, do not fire a call that can
    // only 403. A console full of expected errors trains people to ignore it.
    (signal) =>
      admin
        ? api.get<WebhookEndpoint[]>("/v1/webhook-endpoints", { signal })
        : Promise.resolve([]),
    [orgId, admin],
  );

  if (!admin) return <AdminOnly />;

  const rows = endpoints.data ?? [];

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Webhooks</h1>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={endpoints.reload}>
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      <div className="content">
        {created && <RevealedSecret created={created} onDismiss={() => setCreated(null)} />}

        <CreateEndpoint
          onCreated={(endpoint) => {
            setCreated(endpoint);
            endpoints.reload();
          }}
        />

        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table endpoints-table" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>Endpoint</span>
              <span>Events</span>
              <span>State</span>
              <span className="oc-table__cell--right">Actions</span>
            </div>

            <div className="oc-table__body">
              {endpoints.error && (
                <p className="field-error" style={{ padding: "var(--oc-gutter)" }}>
                  {endpoints.error.message}
                </p>
              )}

              {endpoints.loading && rows.length === 0 && <SkeletonRows columns={4} />}

              {!endpoints.error && rows.length === 0 && !endpoints.loading && (
                <div className="oc-empty">
                  <div className="oc-empty__title">No endpoints yet</div>
                  <div className="oc-empty__hint">
                    An endpoint receives a signed POST for every event you subscribe it to —
                    deliveries, bounces, opens, and anything you record yourself through{" "}
                    <code className="oc-mono-sm">POST /v1/events</code>. Retries run for hours,
                    so a receiver that is briefly down loses nothing.
                  </div>
                </div>
              )}

              {rows.map((endpoint) => (
                <EndpointRow key={endpoint.id} endpoint={endpoint} onChanged={endpoints.reload} />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">
                {rows.length === 0
                  ? "No endpoints"
                  : `${rows.length} endpoint${rows.length === 1 ? "" : "s"}`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * The signing secret, shown once.
 *
 * Only the sealed form is stored, so there is no endpoint that can return it
 * again — the recovery from dismissing this too early is deleting the endpoint
 * and registering it afresh. Says how to verify a delivery here rather than in
 * the docs, because this is the moment someone is about to write that code.
 */
function RevealedSecret({
  created,
  onDismiss,
}: {
  created: CreatedWebhookEndpoint;
  onDismiss: () => void;
}) {
  return (
    <div className="oc-panel oc-panel--attention" role="alert">
      <div className="oc-panel__header">
        <div>
          <div className="oc-label" style={{ marginBottom: 4 }}>
            Signing secret — copy it now
          </div>
          <div className="oc-body-sm oc-text-secondary">
            This is the only time the secret for <strong>{created.url}</strong> will be shown. Only
            an encrypted copy is stored, so it cannot be retrieved later. Verify a delivery by
            computing HMAC-SHA256 over{" "}
            <code className="oc-mono-sm">timestamp + "." + body</code> and comparing it, in constant
            time, against <code className="oc-mono-sm">X-OpenComms-Signature</code>. Reject anything
            whose <code className="oc-mono-sm">X-OpenComms-Timestamp</code> is more than a few
            minutes old, or a captured request stays replayable forever.
          </div>
        </div>
        <button type="button" className="oc-btn oc-btn--sm" onClick={onDismiss}>
          I have stored it
        </button>
      </div>
      <div className="oc-panel__body">
        <div className="oc-dns-value">
          <CopyValue value={created.signing_secret} label="Copy signing secret" className="oc-truncate" />
        </div>
      </div>
    </div>
  );
}

function AdminOnly() {
  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Webhooks</h1>
      </div>
      <div className="content">
        <div className="oc-panel oc-starfield" style={{ flex: 1 }}>
          <div className="oc-empty">
            <div className="oc-empty__title">Endpoints are managed by owners and admins</div>
            <div className="oc-empty__hint">
              An endpoint receives every event in the organization and holds a signing secret, so
              both the list and the controls are limited to the roles that administer it.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function EndpointRow({
  endpoint,
  onChanged,
}: {
  endpoint: WebhookEndpoint;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeliveries, setShowDeliveries] = useState(false);

  const state = endpointState(endpoint);

  const act = async (run: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await run();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : failure);
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    if (
      !window.confirm(
        `Delete ${endpoint.url}? Pending deliveries are dropped and the signing secret cannot be recovered.`,
      )
    ) {
      return;
    }
    void act(
      () => api.delete(`/v1/webhook-endpoints/${encodeURIComponent(endpoint.id)}`),
      "Could not delete the endpoint.",
    );
  };

  const toggle = () =>
    void act(
      () =>
        api.patch(`/v1/webhook-endpoints/${encodeURIComponent(endpoint.id)}`, {
          enabled: !endpoint.enabled,
        }),
      "Could not change the endpoint.",
    );

  return (
    <div className="oc-table__row" role="row" style={{ cursor: "default" }}>
      <span style={{ minWidth: 0 }}>
        <div className="oc-body-sm oc-truncate">{endpoint.url}</div>
        <div className={`oc-mono-sm ${state.className}`}>{state.label}</div>
      </span>

      <span className="oc-body-sm oc-text-secondary oc-truncate">
        {/* An empty list is a subscription to everything, not to nothing —
            written out, because "0 events" would read as broken. */}
        {endpoint.events.length === 0 ? "All events" : endpoint.events.join(", ")}
      </span>

      {/* A switch, not a button that renames itself.
          "Pause" described what clicking would do; this shows what is true now,
          which is the question someone scanning a list of endpoints is asking. */}
      <span className="oc-body-sm oc-text-secondary">
        <label className="oc-toggle">
          <input
            type="checkbox"
            role="switch"
            checked={endpoint.enabled}
            disabled={busy}
            onChange={toggle}
            aria-label={`Deliver to ${endpoint.url}`}
          />
          <span className="oc-toggle__track" />
          {endpoint.enabled ? "Delivering" : "Paused"}
        </label>
      </span>

      <span className="oc-table__cell--right" style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        {/* Collapsed by default. An endpoint that is delivering fine needs no
            history, and expanding every row would put a query per endpoint on
            a screen someone opened to check one of them. */}
        <button
          type="button"
          className="oc-btn oc-btn--ghost oc-btn--sm"
          aria-expanded={showDeliveries}
          onClick={() => setShowDeliveries((open) => !open)}
        >
          {showDeliveries ? "Hide" : "Deliveries"}
        </button>
        <button
          type="button"
          className="oc-btn oc-btn--danger oc-btn--sm"
          disabled={busy}
          onClick={remove}
        >
          Delete
        </button>
        {error && <span className="field-error">{error}</span>}
      </span>

      {showDeliveries && (
        // Spanning every column: the table is a CSS grid, so a child that does
        // not span sits in the first column and squeezes to its width.
        <div style={{ gridColumn: "1 / -1" }}>
          <Deliveries endpoint={endpoint} />
        </div>
      )}
    </div>
  );
}

/**
 * Three different facts, and they read differently.
 *
 * Auto-disabled is the one that matters: an endpoint switched off by repeated
 * failure looks identical to one someone paused, and confusing the two means
 * waiting for events that will never come. Resuming without fixing the receiver
 * just re-enters the same loop, so the line says so.
 */
function endpointState(endpoint: WebhookEndpoint): { label: string; className: string } {
  if (endpoint.disabled_at) {
    return {
      label: `auto-disabled ${new Date(endpoint.disabled_at).toLocaleDateString()} after repeated failures`,
      className: "oc-text-danger",
    };
  }
  if ((endpoint.consecutive_failures ?? 0) > 0) {
    return {
      label: `${endpoint.consecutive_failures} deliveries failing`,
      className: "oc-text-warning",
    };
  }
  return {
    label: `created ${new Date(endpoint.created_at).toLocaleDateString()}`,
    className: "oc-text-muted",
  };
}

function CreateEndpoint({ onCreated }: { onCreated: (endpoint: CreatedWebhookEndpoint) => void }) {
  const [url, setUrl] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!url.trim()) return;

    setBusy(true);
    setError(null);
    try {
      const created = await api.post<CreatedWebhookEndpoint>("/v1/webhook-endpoints", {
        url: url.trim(),
        events: selected,
      });
      setUrl("");
      setSelected([]);
      onCreated(created);
    } catch (err) {
      // The field error, not the envelope's summary.
      //
      // A refused URL arrives as a 422 whose detail is "One or more fields are
      // invalid" — true and useless. The reason the guard took the trouble to
      // write ("169.254.169.254 is link-local (cloud metadata lives here)")
      // is in errors[0].message, and this is the single most important message
      // this screen ever shows: without it, someone who typed an internal
      // address just sees a form that will not submit.
      setError(
        err instanceof ApiError
          ? (err.fields["/url"] ?? err.message)
          : "Could not create the endpoint.",
      );
    } finally {
      setBusy(false);
    }
  };

  const toggleEvent = (type: string) =>
    setSelected((current) =>
      current.includes(type) ? current.filter((t) => t !== type) : [...current, type],
    );

  return (
    <form className="oc-panel" onSubmit={(event) => void submit(event)}>
      <div className="oc-panel__body row" style={{ gap: "var(--oc-space-2)", alignItems: "flex-end" }}>
        <div className="grow login__field">
          <label className="oc-label" htmlFor="endpoint-url">
            Endpoint URL
          </label>
          <input
            id="endpoint-url"
            className="oc-input"
            placeholder="https://example.com/hooks/opencomms"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            // The red border and the announcement are one attribute, so they
            // cannot drift apart. This is the field where the SSRF guard's
            // refusal lands — the most important error in the product, and the
            // one most likely to be read by someone who cannot see the border.
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "endpoint-url-error" : undefined}
          />
        </div>

        <button type="submit" className="oc-btn oc-btn--primary" disabled={busy || !url.trim()}>
          <Icon name="plus" size={12} />
          {busy ? "Adding…" : "Add endpoint"}
        </button>
      </div>

      <div className="oc-panel__body" style={{ paddingTop: 0 }}>
        <div className="oc-label" style={{ marginBottom: 6 }}>
          Events — leave every box clear to receive all of them
        </div>
        <div className="row" style={{ gap: "var(--oc-space-2)", flexWrap: "wrap" }}>
          {DELIVERY_EVENTS.map((type) => (
            <label key={type} className="oc-checkbox">
              <input
                type="checkbox"
                checked={selected.includes(type)}
                onChange={() => toggleEvent(type)}
              />
              <span className="oc-checkbox__box">
                <svg className="oc-checkbox__mark" aria-hidden="true" focusable="false">
                  <use href="#i-check" />
                </svg>
              </span>
              {type}
            </label>
          ))}
        </div>
        <p className="oc-body-sm oc-text-muted" style={{ margin: "var(--oc-space-2) 0 0" }}>
          {/* Says what it does not do, because it is the first question. */}
          Deliveries are HTTPS only and must not resolve to a private or link-local address — an
          endpoint pointing inside your network is refused here rather than after it stops
          arriving. Events recorded through <code className="oc-mono-sm">POST /v1/events</code> are
          delivered too, and trigger nothing else.
        </p>
      </div>

      {error && (
        <div className="oc-panel__body" style={{ paddingTop: 0 }}>
          <p className="field-error" id="endpoint-url-error" role="alert">
            {error}
          </p>
        </div>
      )}
    </form>
  );
}

/**
 * An endpoint's delivery history — the "why", next to the "that".
 *
 * The row above says an endpoint is failing and how many times in a row. This
 * says what the receiving server actually answered, which is the difference
 * between "their TLS certificate expired" and "we are sending them something
 * they reject".
 *
 * Inline rather than a screen of its own: it is about one endpoint, and a
 * separate route would mean navigating away from the list you are triaging.
 *
 * `webhook_deliveries` was unreadable to the application role until 00017 —
 * which is why none of this existed. That migration grants SELECT and adds the
 * row-level security policy the table had never needed before.
 */
function Deliveries({ endpoint }: { endpoint: WebhookEndpoint }) {
  const { orgId, can } = useSession();
  const [abandonedOnly, setAbandonedOnly] = useState(false);

  const { data, error, loading, reload } = useAsync<CursorPage<WebhookDelivery>>(
    (signal) =>
      api.get<CursorPage<WebhookDelivery>>(
        `/v1/webhook-endpoints/${encodeURIComponent(endpoint.id)}/deliveries`,
        { signal, query: { limit: 25, abandoned: abandonedOnly ? "true" : "" } },
      ),
    [orgId, endpoint.id, abandonedOnly],
  );

  const rows = data?.data ?? [];

  return (
    <div className="oc-panel" style={{ margin: "var(--oc-space-2) 0" }}>
      <div className="oc-panel__header">
        <span className="oc-label">Deliveries</span>
        <span className="grow" />
        <label className="oc-body-sm oc-text-secondary row" style={{ gap: "var(--oc-space-2)" }}>
          <input
            type="checkbox"
            checked={abandonedOnly}
            onChange={(event) => setAbandonedOnly(event.target.checked)}
          />
          Abandoned only
        </label>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      <div className="oc-panel__body">
        {error && <p className="field-error">{error.message}</p>}

        {loading && rows.length === 0 && (
          <span className="oc-body-sm oc-text-secondary">Loading…</span>
        )}

        {!error && !loading && rows.length === 0 && (
          <span className="oc-body-sm oc-text-muted-aa">
            {abandonedOnly
              ? "Nothing abandoned. Every delivery to this endpoint either arrived or is still being retried."
              : "No deliveries yet. One appears here the first time an event matches this endpoint."}
          </span>
        )}

        {rows.map((delivery) => (
          <DeliveryRow
            key={delivery.id}
            endpointId={endpoint.id}
            delivery={delivery}
            replayable={can("webhooks:manage")}
            onReplayed={reload}
          />
        ))}
      </div>
    </div>
  );
}

function DeliveryRow({
  endpointId,
  delivery,
  replayable,
  onReplayed,
}: {
  endpointId: string;
  delivery: WebhookDelivery;
  replayable: boolean;
  onReplayed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const replay = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/v1/webhook-endpoints/${encodeURIComponent(endpointId)}/deliveries/${encodeURIComponent(delivery.id)}/replay`,
        {},
      );
      onReplayed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not replay the delivery.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="row"
      style={{
        gap: "var(--oc-space-3)",
        padding: "var(--oc-space-2) 0",
        borderBottom: "1px solid var(--oc-border)",
        alignItems: "flex-start",
      }}
    >
      <span className={`oc-chip oc-mono-sm ${DELIVERY_TONE[delivery.state]}`}>
        {delivery.state}
      </span>

      <span className="oc-body-sm grow" style={{ minWidth: 0 }}>
        <span className="oc-mono-sm">{delivery.event_type}</span>
        {/* The status code and the error together, because either alone is
            ambiguous: a 500 with no body and a connection refused look the
            same in a count and mean different things to fix. */}
        {delivery.last_error && (
          <div className="oc-mono-sm oc-text-secondary oc-truncate" title={delivery.last_error}>
            {delivery.status_code != null && `${delivery.status_code} · `}
            {delivery.last_error}
          </div>
        )}
        {!delivery.last_error && delivery.status_code != null && (
          <div className="oc-mono-sm oc-text-secondary">{delivery.status_code}</div>
        )}
        {error && (
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
      </span>

      <span className="oc-mono-sm oc-text-secondary" style={{ whiteSpace: "nowrap" }}>
        {delivery.attempt > 0 &&
          `${delivery.attempt} ${delivery.attempt === 1 ? "attempt" : "attempts"}`}
      </span>

      <span className="oc-mono-sm oc-text-secondary" style={{ whiteSpace: "nowrap" }}>
        {new Date(delivery.created_at).toLocaleString()}
      </span>

      <span style={{ minWidth: 80, textAlign: "right" }}>
        {/* Only an abandoned delivery can be replayed. One still retrying does
            not need it, and one that succeeded must never be resent — the
            server refuses both, and offering the button anyway would be an
            invitation to find that out. */}
        {delivery.state === "abandoned" && replayable && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            disabled={busy}
            onClick={() => void replay()}
          >
            {busy ? "Replaying…" : "Replay"}
          </button>
        )}
      </span>
    </div>
  );
}

/** The four states, in the same colour vocabulary the rest of the dashboard uses. */
const DELIVERY_TONE: Record<string, string> = {
  delivered: "oc-text-success",
  abandoned: "oc-text-danger",
  retrying: "oc-text-warning",
  pending: "oc-text-secondary",
};
